import React, { useState, useCallback, useRef } from 'react';
import { collection, addDoc, Timestamp, serverTimestamp }
  from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/firebaseConfig';
import './AddEventPage.css'; // reuse the same form styles

const CATEGORIES = [
  'Service',
  'Accessories',
  'Spare Parts',
  'Insurance',
  'Detailing',
  'Food & Stay',
  'Other',
];

const INITIAL = {
  title:       '',
  partner:     '',
  code:        '',
  discount:    '',
  description: '',
  redeemUrl:   '',
  validUntil:  '',
  category:    '',
  isActive:    true,
};

export default function AddCouponPage() {
  const [form,       setForm]       = useState(INITIAL);
  const [errors,     setErrors]     = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [toast,      setToast]      = useState(null);
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setToast({ type: 'error', message: '❌ Please select an image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setToast({ type: 'error', message: '❌ Image must be under 5 MB.' });
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }, []);

  const clearImage = useCallback(() => {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  }, []);

  const validate = useCallback(() => {
    const e = {};
    if (!form.title.trim())      e.title      = 'Title is required.';
    if (!form.partner.trim())    e.partner    = 'Partner name is required.';
    if (!form.code.trim())       e.code       = 'Coupon code is required.';
    if (!form.discount.trim())   e.discount   = 'Discount label is required.';
    if (!form.validUntil)        e.validUntil = 'Valid-until date is required.';
    if (form.redeemUrl.trim()) {
      try { new URL(form.redeemUrl.trim()); }
      catch { e.redeemUrl = 'Must be a valid URL (https://…).'; }
    }
    return e;
  }, [form]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSubmitting(true);
    setToast(null);

    try {
      // Upload the partner logo first (if chosen) and grab the URL.
      let imageUrl = null;
      if (imageFile) {
        const ext = (imageFile.name.split('.').pop() || 'jpg').toLowerCase();
        const path = `coupons/${Date.now()}.${ext}`;
        const storageRef = ref(storage, path);
        await uploadBytes(storageRef, imageFile, {
          contentType: imageFile.type,
        });
        imageUrl = await getDownloadURL(storageRef);
      }

      await addDoc(collection(db, 'coupons'), {
        title:       form.title.trim(),
        partner:     form.partner.trim(),
        code:        form.code.trim().toUpperCase(),
        discount:    form.discount.trim(),
        description: form.description.trim(),
        redeemUrl:   form.redeemUrl.trim() || null,
        imageUrl,
        validUntil:  Timestamp.fromDate(new Date(form.validUntil)),
        category:    form.category || null,
        isActive:    form.isActive,
        created_at:  serverTimestamp(),
      });

      setToast({
        type: 'success',
        message: `✅ "${form.title.trim()}" coupon published.`,
      });
      setForm(INITIAL);
      clearImage();
    } catch (err) {
      setToast({ type: 'error', message: `❌ Failed: ${err.message}` });
    } finally {
      setSubmitting(false);
    }
  }, [form, validate]);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Add Coupon</h1>
          <p className="page-subtitle">
            Publish a new member offer or discount.
          </p>
        </div>
      </div>

      {toast && (
        <div
          className={`amf-toast amf-toast--${toast.type}`}
          style={{ marginBottom: 24 }}
        >
          {toast.message}
          <button
            className="amf-toast-close"
            onClick={() => setToast(null)}
          >
            ×
          </button>
        </div>
      )}

      <form className="aep-form" onSubmit={handleSubmit} noValidate>
        <div className="aep-grid">

          {/* Left column */}
          <div className="aep-col">
            <Field label="Coupon Title" required error={errors.title}>
              <input
                name="title"
                className={`amf-input ${errors.title ? 'amf-input--error' : ''}`}
                placeholder="e.g. 20% off Annual Service"
                value={form.title} onChange={handleChange}
              />
            </Field>

            <Field label="Partner Name" required error={errors.partner}>
              <input
                name="partner"
                className={`amf-input ${errors.partner ? 'amf-input--error' : ''}`}
                placeholder="e.g. Maruti Suzuki Calicut"
                value={form.partner} onChange={handleChange}
              />
            </Field>

            <Field label="Coupon Code" required error={errors.code}>
              <input
                name="code"
                className={`amf-input ${errors.code ? 'amf-input--error' : ''}`}
                placeholder="e.g. FRONX20"
                style={{ textTransform: 'uppercase' }}
                value={form.code} onChange={handleChange}
              />
              <p className="amf-hint">
                Members copy this code to redeem with the partner.
              </p>
            </Field>

            <Field label="Description">
              <textarea
                name="description"
                className="amf-input aep-textarea"
                placeholder="Terms, conditions, how to redeem…"
                rows={4}
                value={form.description} onChange={handleChange}
              />
            </Field>

            <Field label="Redeem URL" error={errors.redeemUrl}>
              <input
                name="redeemUrl"
                type="url"
                className={`amf-input ${errors.redeemUrl ? 'amf-input--error' : ''}`}
                placeholder="https://partner.com/redeem-fronx"
                value={form.redeemUrl} onChange={handleChange}
              />
              <p className="amf-hint">
                Optional. Members can tap the coupon to open this link
                (partner's page, booking form, WhatsApp chat, etc.).
              </p>
            </Field>
          </div>

          {/* Right column */}
          <div className="aep-col">
            <Field
              label="Discount Badge Text"
              required
              error={errors.discount}
            >
              <input
                name="discount"
                className={`amf-input ${errors.discount ? 'amf-input--error' : ''}`}
                placeholder="e.g. 20% OFF, ₹500 OFF, FREE"
                value={form.discount} onChange={handleChange}
              />
              <p className="amf-hint">
                Shown as a coloured badge on the coupon card.
              </p>
            </Field>

            <Field
              label="Valid Until"
              required
              error={errors.validUntil}
            >
              <input
                name="validUntil"
                type="date"
                className={`amf-input ${errors.validUntil ? 'amf-input--error' : ''}`}
                value={form.validUntil} onChange={handleChange}
              />
            </Field>

            <Field label="Category">
              <select
                name="category"
                className="amf-select"
                value={form.category} onChange={handleChange}
              >
                <option value="">— None —</option>
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </Field>

            <Field label="Partner Logo / Banner">
              {imagePreview ? (
                <div style={{
                  position: 'relative',
                  borderRadius: 8,
                  overflow: 'hidden',
                  border: '1px solid rgba(212, 181, 116, 0.25)',
                }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      width: '100%',
                      maxHeight: 180,
                      objectFit: 'contain',
                      background: '#0F0F0F',
                      display: 'block',
                    }}
                  />
                  <button
                    type="button"
                    onClick={clearImage}
                    style={{
                      position: 'absolute',
                      top: 8, right: 8,
                      padding: '6px 12px',
                      background: 'rgba(0,0,0,0.75)',
                      border: '1px solid #E63946',
                      borderRadius: 6,
                      color: '#E63946',
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Remove
                  </button>
                  <div style={{
                    padding: '8px 12px',
                    fontSize: 11,
                    color: '#B79F6D',
                    background: '#0F0F0F',
                    fontFamily: 'monospace',
                  }}>
                    {imageFile?.name} · {imageFile && (imageFile.size / 1024).toFixed(0)} KB
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    width: '100%',
                    padding: '24px 16px',
                    background: '#161618',
                    border: '2px dashed rgba(212, 181, 116, 0.35)',
                    borderRadius: 8,
                    color: '#D8C8A4',
                    fontSize: 13,
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <span style={{ fontSize: 24 }}>🏷️</span>
                  <span style={{ fontWeight: 700 }}>Click to upload logo</span>
                  <span style={{ color: '#B79F6D', fontSize: 11 }}>
                    Optional · JPG, PNG, or WebP · max 5 MB
                  </span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageChange}
              />
            </Field>

            <Field label="Active">
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  cursor: 'pointer',
                }}
              >
                <input
                  name="isActive"
                  type="checkbox"
                  checked={form.isActive}
                  onChange={handleChange}
                  style={{ width: 18, height: 18 }}
                />
                <span style={{ color: '#D8C8A4', fontSize: 14 }}>
                  Visible to members immediately
                </span>
              </label>
            </Field>
          </div>
        </div>

        <div className="aep-submit-row">
          <button
            type="submit"
            className="amf-submit-btn"
            disabled={submitting}
          >
            {submitting
              ? <><span className="amf-spinner" /> Publishing…</>
              : 'Publish Coupon'}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, error, children, style }) {
  return (
    <div className="amf-field" style={style}>
      <label className="amf-label">
        {label}
        {required && <span className="amf-required"> *</span>}
      </label>
      {children}
      {error && <p className="amf-error-text">{error}</p>}
    </div>
  );
}
